import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const LEGAL_VERSION = "2026-05-22";

const DOC_TITLES = {
    terms: "Beach Bum Pass Terms & Conditions",
    privacy: "Beach Bum Pass Privacy Policy",
    cookies: "Beach Bum Pass Cookie Policy",
};

function PolicySection({ title, children }) {
    return (
        <section className="legal-doc-section">
            <h3>{title}</h3>
            <div>{children}</div>
        </section>
    );
}

function TermsContent() {
    return (
        <>
            <p className="legal-doc-intro">
                These Terms & Conditions explain the basic rules for purchasing and using Beach Bum Pass passes,
                QR codes, pass perks, vendor redemptions, and related account features.
            </p>

            <PolicySection title="1. Business and service overview">
                <p>
                    Beach Bum Pass is operated by Florida Beach Bum Ventures, LLC DBA Beach Bum Pass. Beach Bum Pass provides access, discounts,
                    pass perks, and redemption tools connected to participating vendors, attractions, and experiences.
                </p>
            </PolicySection>

            <PolicySection title="2. Pass purchase and account access">
                <p>
                    A Beach Bum Pass may require a user account so your pass, QR code, crew passes, purchase history, and claim codes can be connected
                    to your profile. You are responsible for keeping your login information secure and for using the correct email address during purchase.
                </p>
            </PolicySection>

            <PolicySection title="3. Activation and trip windows">
                <p>
                    Passes are intended for the selected trip window. A pass may remain unactivated until the first valid scan at a participating vendor.
                    Once activated, the pass validity window is based on the purchased pass duration and the rules shown at checkout or inside your account.
                </p>
            </PolicySection>

            <PolicySection title="4. Child and crew passes">
                <p>
                    Baby Bum, Little Bum, and Grommet passes are age-based pass types. Some activities may have minimum age rules, height rules,
                    safety rules, reservation rules, or other vendor-specific restrictions. A pass purchase does not override an activity provider’s safety policy.
                </p>
            </PolicySection>

            <PolicySection title="5. Vendor rules and reservations">
                <p>
                    Participating vendors may require reservations, waivers, arrival windows, weather checks, capacity limits, or other instructions.
                    Users are responsible for reviewing vendor instructions before arriving. Beach Bum Pass cannot guarantee availability for every activity at every moment.
                </p>
            </PolicySection>

            <PolicySection title="6. QR codes and redemptions">
                <p>
                    Your QR code is used to verify and redeem your pass. Do not share screenshots or pass IDs with people who are not authorized to use your pass.
                    Vendor scans may be logged with pass status, vendor name, activity, timestamp, and scan result for fraud prevention and redemption tracking.
                </p>
            </PolicySection>

            <PolicySection title="7. Payments and checkout">
                <p>
                    Payments are processed by a secure third-party payment processor. Beach Bum Pass does not store full credit card numbers in the app.
                    Prices, taxes, pass duration, and included experiences may change. The price shown at checkout controls the purchase in that session.
                </p>
            </PolicySection>

            <PolicySection title="8. Refunds, weather, and cancellations">
                <p>
                    Some purchases may be final once completed, unless Beach Bum Pass approves an exception or applicable law requires otherwise.
                    Weather, vendor closures, maintenance, and capacity limits may affect activity availability. Contact Beach Bum Pass support if a major issue affects your trip.
                </p>
            </PolicySection>

            <PolicySection title="9. Acceptable use">
                <p>
                    You may not duplicate, sell, abuse, tamper with, reverse engineer, or fraudulently use any Beach Bum Pass QR code, account, claim code,
                    vendor portal, or redemption tool. Misuse may result in pass cancellation or account restriction.
                </p>
            </PolicySection>

            <PolicySection title="10. Updates to these terms">
                <p>
                    Beach Bum Pass may update these terms as the product, vendors, checkout tools, or legal requirements change. The version date shown
                    in this modal identifies the terms presented during checkout.
                </p>
            </PolicySection>
        </>
    );
}

function PrivacyContent() {
    return (
        <>
            <p className="legal-doc-intro">
                This Privacy Policy explains the types of information Beach Bum Pass may collect and how that information is used to run the app,
                process purchases, manage passes, and support vendor redemptions.
            </p>

            <PolicySection title="1. Information you provide">
                <p>
                    We may collect account details such as name, email address, phone number, profile information, login provider, pass holder names,
                    child pass assignment details, trip dates, and support messages you submit.
                </p>
            </PolicySection>

            <PolicySection title="2. Purchase and payment information">
                <p>
                    Checkout may collect payment-related details through a third-party payment processor. Beach Bum Pass may receive transaction status,
                    order totals, pass selections, billing details, and payment confirmation data, but does not store full card numbers in the app.
                </p>
            </PolicySection>

            <PolicySection title="3. Pass and redemption information">
                <p>
                    We may collect pass IDs, QR payloads, activation dates, redemption history, scan status, vendor name, activity name, reservation references,
                    and related logs so passes can be verified and vendors can track valid use.
                </p>
            </PolicySection>

            <PolicySection title="4. Device and usage information">
                <p>
                    We may collect basic technical information such as browser type, app platform, approximate device information, error logs, and usage events
                    to keep the website and app working properly.
                </p>
            </PolicySection>

            <PolicySection title="5. How information is used">
                <p>
                    We use information to create accounts, process checkout, issue passes, display QR codes, prevent fraud, support vendors, provide customer support,
                    improve the product, and communicate important account or purchase updates.
                </p>
            </PolicySection>

            <PolicySection title="6. Sharing information">
                <p>
                    We may share limited information with payment processors, authentication providers, hosting/database vendors, email providers, and participating vendors
                    when needed to provide the service. Vendors should only receive information needed to verify or track a redemption.
                </p>
            </PolicySection>

            <PolicySection title="7. Data security">
                <p>
                    We use reasonable technical and organizational safeguards designed to protect account, pass, and purchase data. No internet service can guarantee
                    absolute security, so users should also keep passwords and account access secure.
                </p>
            </PolicySection>

            <PolicySection title="8. Your choices">
                <p>
                    You may update certain account details in your account page. You may contact Beach Bum Pass to request help with account access, data questions,
                    or privacy-related requests.
                </p>
            </PolicySection>

            <PolicySection title="9. Policy updates">
                <p>
                    We may update this Privacy Policy as the product, vendors, processors, or legal requirements change. The version date shown in this modal identifies
                    the policy currently presented inside Beach Bum Pass.
                </p>
            </PolicySection>
        </>
    );
}

function CookieContent() {
    return (
        <>
            <p className="legal-doc-intro">
                This Cookie Policy explains how Beach Bum Pass may use cookies, local storage, session storage, and similar technologies on the website and app.
            </p>

            <PolicySection title="1. What cookies and storage do">
                <p>
                    Cookies and browser storage help keep users signed in, remember trip and pass builder choices, support checkout, protect accounts,
                    and improve how the site performs.
                </p>
            </PolicySection>

            <PolicySection title="2. Essential cookies and storage">
                <p>
                    Some storage is required for authentication, security, checkout, routing, saved form state, and pass-builder functionality. Disabling essential
                    storage may prevent login, checkout, QR display, or account features from working correctly.
                </p>
            </PolicySection>

            <PolicySection title="3. Analytics and performance">
                <p>
                    Beach Bum Pass may use performance or analytics tools to understand errors, page usage, app behavior, and feature performance. These tools help
                    improve the product and fix bugs.
                </p>
            </PolicySection>

            <PolicySection title="4. Third-party services">
                <p>
                    Authentication providers, payment processors, hosting providers, map/location tools, analytics tools, or embedded services may use their own cookies
                    or similar technologies according to their own policies.
                </p>
            </PolicySection>

            <PolicySection title="5. Managing cookies">
                <p>
                    You can manage cookies and site storage through your browser or device settings. Clearing storage may sign you out, reset saved pass-builder choices,
                    or require you to restart checkout.
                </p>
            </PolicySection>

            <PolicySection title="6. Updates to this policy">
                <p>
                    We may update this Cookie Policy as the website, app, analytics tools, or checkout systems change. The version date shown in this modal identifies
                    the current version.
                </p>
            </PolicySection>
        </>
    );
}

function LegalContent({ type }) {
    if (type === "privacy") return <PrivacyContent />;
    if (type === "cookies") return <CookieContent />;
    return <TermsContent />;
}

export function LegalDocumentModal({ type = "terms", onClose, requireScroll = false, onAccept = null }) {
    const [hasReachedEnd, setHasReachedEnd] = useState(!requireScroll);
    const bodyRef = useRef(null);
    const title = DOC_TITLES[type] || DOC_TITLES.terms;

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    useEffect(() => {
        if (!requireScroll) return;
        setHasReachedEnd(false);
    }, [type, requireScroll]);

    const handleScroll = () => {
        if (!requireScroll || hasReachedEnd) return;
        const el = bodyRef.current;
        if (!el) return;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining <= 18) setHasReachedEnd(true);
    };

    if (typeof document === "undefined") return null;

    return createPortal(
        <div className="legal-modal-backdrop" role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose?.();
        }}>
            <section className="legal-modal" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title">
                <header className="legal-modal-head">
                    <div>
                        <p className="legal-modal-kicker">Beach Bum Pass</p>
                        <h2 id="legal-modal-title">{title}</h2>
                        <span>Version {LEGAL_VERSION}</span>
                    </div>
                    <button className="legal-modal-close" type="button" onClick={onClose} aria-label="Close legal document">
                        ×
                    </button>
                </header>

                <div className="legal-modal-body" ref={bodyRef} onScroll={handleScroll} tabIndex={0}>
                    <LegalContent type={type} />
                    {requireScroll ? <div className="legal-scroll-anchor">End of document</div> : null}
                </div>

                <footer className="legal-modal-foot">
                    {requireScroll ? (
                        <p className={hasReachedEnd ? "legal-scroll-note is-ready" : "legal-scroll-note"}>
                            {hasReachedEnd
                                ? "You reached the bottom. You can accept and continue."
                                : "Scroll to the bottom to unlock acceptance."}
                        </p>
                    ) : null}

                    <div className="legal-modal-actions">
                        <button className="legal-secondary-btn" type="button" onClick={onClose}>
                            Close
                        </button>
                        {onAccept ? (
                            <button
                                className="legal-primary-btn"
                                type="button"
                                disabled={requireScroll && !hasReachedEnd}
                                onClick={() => {
                                    onAccept?.();
                                    onClose?.();
                                }}
                            >
                                Accept Terms
                            </button>
                        ) : null}
                    </div>
                </footer>
            </section>
        </div>,
        document.body
    );
}

export function LegalTextLinks({ className = "", includeCookies = true }) {
    const [activeDoc, setActiveDoc] = useState(null);

    return (
        <span className={`legal-inline-links ${className}`.trim()}>
            <button type="button" onClick={() => setActiveDoc("terms")}>Terms & Conditions</button>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={() => setActiveDoc("privacy")}>Privacy Policy</button>
            {includeCookies ? (
                <>
                    <span aria-hidden="true">·</span>
                    <button type="button" onClick={() => setActiveDoc("cookies")}>Cookie Policy</button>
                </>
            ) : null}

            {activeDoc ? (
                <LegalDocumentModal type={activeDoc} onClose={() => setActiveDoc(null)} />
            ) : null}
        </span>
    );
}

export function LegalFooterLinks() {
    const [activeDoc, setActiveDoc] = useState(null);

    return (
        <>
            <button className="footer-link-button" type="button" onClick={() => setActiveDoc("terms")}>Terms</button>
            <button className="footer-link-button" type="button" onClick={() => setActiveDoc("privacy")}>Privacy</button>
            <button className="footer-link-button" type="button" onClick={() => setActiveDoc("cookies")}>Cookie Policy</button>

            {activeDoc ? (
                <LegalDocumentModal type={activeDoc} onClose={() => setActiveDoc(null)} />
            ) : null}
        </>
    );
}

export function LegalConsentGate({ accepted, onAccept, className = "" }) {
    const [showTerms, setShowTerms] = useState(false);

    const statusText = useMemo(() => {
        if (accepted) return "Terms accepted. You can continue to secure payment.";
        return "Open and scroll through the terms to unlock checkout.";
    }, [accepted]);

    return (
        <div className={`legal-consent-gate ${accepted ? "is-accepted" : ""} ${className}`.trim()}>
            <button className="legal-consent-main" type="button" onClick={() => setShowTerms(true)}>
                <span className="legal-consent-check" aria-hidden="true">{accepted ? "✓" : ""}</span>
                <span>
                    <strong>You agree to BBP Terms and Conditions</strong>
                    <small>{statusText}</small>
                </span>
            </button>

            <LegalTextLinks className="legal-consent-links" />

            {showTerms ? (
                <LegalDocumentModal
                    type="terms"
                    requireScroll
                    onAccept={onAccept}
                    onClose={() => setShowTerms(false)}
                />
            ) : null}
        </div>
    );
}
